<template>
  <fullscreen-dialog
    v-model="model"
    :title="`${$t('report')} ${toLatin(employee.name)} ${toLatin(employee.lastname)}`"
    :subtitle="`${$t('month')}: ${month}/${year}`"
    color="primary"
    hide-footer
    hide-padding
  >
    <template v-slot:filters>
      <page-toolbar :searchable="false">
        <template v-slot:filters>
          <v-col cols="auto">
            <month-picker
              v-model="date"
              color="white"
              hide-details
              @updatedMonthDates="monthDates = $event"
            />
          </v-col>
          <v-col cols="auto">
            <text-field
              v-model="search"
              kind="search"
              :label="$t('search')"
              :placeholder="$t('search')"
              color="white"
              hide-details
            />
          </v-col>
          <v-col cols="auto" style="min-width: 220px">
            <dropdown
              v-model="selectedRestaurants"
              :items="restaurants"
              :label="$t('restaurants')"
              color="white"
              multiple
              hide-details
            />
          </v-col>
          <v-col cols="auto">
            <v-checkbox
              :label="$t('show_all_days')"
              hide-details
              v-model="allDayFirm"
              dense
            />
          </v-col>
          <v-col>
            <v-row no-gutters justify="end" align="center">
              <excel-button @click="exportExcel" class="mr-2" />
              <print-button @click="printReport" :loading="isPrinting" />
            </v-row>
          </v-col>
        </template>
      </page-toolbar>
    </template>

    <v-simple-table v-if="userReport" dense class="ordera-box-shadow">
      <template v-slot:default>
        <thead>
          <tr>
            <th class="text-left">{{ $t("date") }}</th>
            <th class="text-left">{{ $t("shifts") }}</th>
            <th class="text-left">{{ $t("title") }}</th>
            <th class="text-left">{{ $t("restaurants") }}</th>
            <th class="text-left">{{ $t("quantity") }}</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="(row, i) in tableRows" :key="i">
            <td>{{ formatDateDisplay(row.date) }}.</td>
            <td>{{ row.shift }}</td>
            <td>{{ toLatin(row.meal_name) }}</td>
            <td>{{ toLatin(row.restaurant_name) }}</td>
            <td>{{ row.quantity }}</td>
          </tr>
          <tr>
            <td class="font-weight-bold">{{ $t("total") }}</td>
            <td colspan="3"></td>
            <td class="font-weight-bold">{{ foodTotal }}</td>
          </tr>
        </tbody>
      </template>
    </v-simple-table>
  </fullscreen-dialog>
</template>

<script>
import hasModel from "@/mixins/hasModel";
import hasExcel from "@/mixins/hasExcel";
import hasPrinting from "@/mixins/hasPrinting";
import userService from "@/services/user";
import FullscreenDialog from "@/components/dialogs/FullscreenDialog.vue";

export default {
  mixins: [hasModel, hasExcel, hasPrinting],
  components: { FullscreenDialog },
  props: {
    employee: { type: Object, required: true },
  },
  data: () => ({
    userReportPlane: [],
    userReport: [],
    date: new Date().toISOString().substr(0, 7),
    monthDates: [],
    search: "",
    allDayFirm: false,
    selectedRestaurants: [],
  }),
  created() {
    this.date = this.dates.from.slice(0, 7);
  },
  computed: {
    year() {
      return this.date.slice(0, 4);
    },
    month() {
      return this.date.slice(5, 7);
    },
    monthStart() {
      return `${this.date}-01`;
    },
    monthEnd() {
      const lastDay = new Date(+this.year, +this.month, 0).getDate();
      return `${this.date}-${String(lastDay).padStart(2, "0")}`;
    },
    dates() {
      return this.$store.state.report.filters.dates;
    },
    restaurants() {
      return [
        ...new Set(
          this.userReportPlane.map((item) => item.restaurant_name).filter(Boolean)
        ),
      ].sort();
    },
    filteredReportPlane() {
      if (!this.selectedRestaurants.length) return this.userReportPlane;
      return this.userReportPlane.filter((item) =>
        this.selectedRestaurants.includes(item.restaurant_name)
      );
    },
    foodTotal() {
      return this.filteredReportPlane.reduce((sum, item) => sum + item.quantity, 0);
    },
    orders() {
      if (!this.allDayFirm) return this.userReport;
      const daysInMonth = new Date(+this.year, +this.month, 0).getDate();
      return Array.from({ length: daysInMonth }, (_, i) => {
        const dateStr = `${this.year}-${this.month}-${String(i + 1).padStart(2, "0")}`;
        return (
          this.userReport.find((g) => g.date === dateStr) || { date: dateStr, orders: [] }
        );
      });
    },
    tableRows() {
      const items = this.orders.flatMap((item) => {
        const foods = (item.orders || []).flatMap((order) => order.foods);
        const filteredFoods = this.selectedRestaurants.length
          ? foods.filter((food) =>
              this.selectedRestaurants.includes(food.restaurant_name)
            )
          : foods;

        if (!filteredFoods.length)
          return [
            {
              date: item.date,
              shift: "",
              meal_name: "",
              restaurant_name: "",
              quantity: "",
            },
          ];

        return filteredFoods.map((food) => ({
          date: item.date,
          shift: food.shift,
          meal_name: food.meal_name,
          restaurant_name: food.restaurant_name,
          quantity: food.quantity,
        }));
      });
      return this.customSearch(
        items,
        [{ value: "meal_name" }, { value: "restaurant_name" }],
        this.search
      );
    },
    reportData() {
      const headers = [
        { text: this.$t("date"), value: "date", align: "center", print: true },
        { text: this.$t("shifts"), value: "shift", align: "center", print: true },
        { text: this.$t("title"), value: "meal_name", align: "start", print: true },
        {
          text: this.$t("restaurants"),
          value: "restaurant_name",
          align: "start",
          print: true,
        },
        { text: this.$t("quantity"), value: "quantity", align: "center", print: true },
      ];
      const items = [
        ...this.filteredReportPlane.map((item) => ({
          date: this.formatDateDisplay(item.date),
          shift: item.shift,
          meal_name: this.toLatin(item.meal_name),
          restaurant_name: this.toLatin(item.restaurant_name),
          quantity: item.quantity,
        })),
        {
          date: this.$t("total"),
          shift: "",
          meal_name: "",
          restaurant_name: "",
          quantity: this.foodTotal,
        },
      ];
      const title = `${this.$t("report")} ${this.employee.name} ${
        this.employee.lastname
      }`;
      const subtitle = `${this.$t("month")}: ${this.month}/${this.year}`;
      return { headers, items, title, subtitle };
    },
  },
  methods: {
    formatDateDisplay(dateStr) {
      return dateStr.slice(8, 10);
    },
    exportExcel() {
      this.setExcel();
      const { headers, items, title, subtitle } = this.reportData;
      this.downloadExcel({
        fileName: `${this.employee.name}_${this.employee.lastname}_${this.month}_${this.year}.xlsx`,
        sheets: [{ name: this.$t("report"), headers, items, title, subtitle }],
      });
    },
    printReport() {
      this.setPrinter();
      const { headers, items, title, subtitle } = this.reportData;
      this.printDocument({
        pageTitle: title,
        title,
        subtitle,
        tables: [
          {
            headers,
            items,
            title: "",
            tableStyle:
              "width: 100%; border-collapse: collapse; font-size: 12px; margin-top: 20px;",
          },
        ],
      });
    },
    getEmployeeReport() {
      userService
        .getOrdersByMonth({
          month: this.month,
          year: this.year,
          date_from: this.monthStart,
          date_to: this.monthEnd,
          user_id: this.employee.id,
        })
        .then((res) => {
          this.userReportPlane = res.data;
          this.userReport = res.data.reduce((acc, item) => {
            const dateGroup = acc.find((d) => d.date === item.date);
            if (!dateGroup) {
              acc.push({ date: item.date, orders: [{ id: item.id, foods: [item] }] });
            } else {
              const orderGroup = dateGroup.orders.find((o) => o.id === item.id);
              if (orderGroup) orderGroup.foods.push(item);
              else dateGroup.orders.push({ id: item.id, foods: [item] });
            }
            return acc;
          }, []);
          this.selectedRestaurants = [...this.restaurants];
        });
    },
  },
  watch: {
    employee: {
      handler() {
        if (this.employee.id) this.getEmployeeReport();
      },
      deep: true,
    },
    date() {
      this.getEmployeeReport();
    },
  },
};
</script>
